import React from "react";

const CorporateCommunications = () => {
  const keyInitiatives = [
    {
      title: "Digital Transformation",
      description: "Implementing smart grid technologies, advanced metering infrastructure, and customer self-service portals to enhance operational efficiency and customer experience.",
      icon: "🔄"
    },
    {
      title: "Community Engagement",
      description: "Regular town hall meetings, customer education programs, and transparent communication channels to foster strong community relationships.",
      icon: "🤝"
    },
    {
      title: "Sustainability Focus",
      description: "Increasing investment in renewable energy sources and promoting energy conservation initiatives across our franchise areas.",
      icon: "🌱"
    },
    {
      title: "Safety First",
      description: "Continuous network safety audits, public safety campaigns, and employee training programs to ensure safe electricity distribution.",
      icon: "🛡️"
    }
  ];

  const achievements = [
    { metric: "90%", label: "Reduction in Technical Losses" },
    { metric: "10k+", label: "KM of New Distribution Lines" },
    { metric: "24/7", label: "Customer Support Operational" },
    { metric: "95%", label: "Complaints Resolved Instantly" }
  ];

  const completedProjects = [
    { name: "COMMISSIONING OF COUNTRY HOME 33Kv FEEDER", link: "https://www.linkedin.com/feed/update/urn:li:activity:7466079287071129601" },
    { name: "EKPOMA 33Kv", link: "https://www.linkedin.com/feed/update/urn:li:activity:7421537297814249472" },
    { name: "BEDC COMMISSIONS AN INJECTION SUBSTATION AT ISUNAMBOGU, DELTA STATE", link: "https://www.linkedin.com/feed/update/urn:li:activity:7407708913175011328" },
  ];

  return (
    <div className="w-full min-h-screen bg-white font-sans">
      {/* Hero Section */}
      <div className="relative bg-[#14532d] text-white py-12 md:py-16 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            backgroundSize: '60px 60px'
          }}> 
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 relative z-10">
          <div className="flex flex-col lg:flex-row items-center justify-between">
            <div className="lg:w-2/3">
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold leading-tight uppercase tracking-tight">
              Corporate <span className="text-yellow-400">Communications</span>
              </h1>
              <div className="w-20 h-1.5 bg-yellow-400 rounded-full mb-6"></div>
            </div>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 md:py-8">

        {/* Intro Banner */}
        <div className="bg-black text-white p-4 sm:p-6 md:p-8 mb-6 shadow-xl rounded-xl md:rounded-2xl border-l-[6px] md:border-l-[8px] border-green-700 relative overflow-hidden group">
          <div className="absolute top-0 right-0 w-64 h-64 bg-green-700/10 rounded-full -mr-32 -mt-32 group-hover:scale-110 transition-transform duration-700"></div>
          <h2 className="text-base sm:text-lg md:text-xl font-black uppercase mb-3 tracking-tighter relative z-10">Our Strategic Direction</h2>
          <p className="text-xs sm:text-sm md:text-base font-medium leading-relaxed opacity-80 relative z-10">
            Our future business model is tilted towards efficient, customer-centric technology-enabled solutions.
            We're focused on reducing dependency on grid power through distributed generation, improving system
            efficiency, and achieving 24/7 reliable power supply across Edo, Delta, Ondo, and Ekiti states.
          </p>
        </div>

        {/* Key Metrics Section */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 mb-6">
          {achievements.map((item, index) => (
            <div key={index} className="bg-gray-50 p-4 sm:p-5 rounded-xl border border-gray-100 text-center hover:bg-white hover:shadow-lg hover:-translate-y-1 transition-all duration-300 group">
              <div className="text-xl sm:text-2xl md:text-3xl font-black text-green-700 mb-1 group-hover:text-yellow-500 transition-colors uppercase tracking-tight">{item.metric}</div>
              <div className="text-[10px] font-black uppercase text-gray-400 tracking-widest leading-tight">{item.label}</div>
            </div>
          ))}
        </div>

        {/* Core Vision Section */}
        <div className="bg-white rounded-xl md:rounded-2xl shadow-xl border border-gray-100 overflow-hidden mb-6">
          <div className="p-4 sm:p-6 md:p-8">
            <h2 className="text-lg sm:text-xl md:text-2xl font-black text-black mb-4 uppercase tracking-tighter">
              Transformative <span className="text-green-700">Impact</span>
            </h2>

            <div className="space-y-4">
              <p className="text-gray-600 text-xs sm:text-sm md:text-base font-medium leading-relaxed italic border-l-4 border-yellow-400 pl-4">
                Since our inception, BEDC has brought about significant improvements in service delivery and
                electricity infrastructure. Outputting better quality electricity supply for all customers.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="p-5 sm:p-6 bg-[#14532d] rounded-xl text-white shadow-xl relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -mr-16 -mt-16"></div>
                  <h3 className="text-[10px] font-black uppercase tracking-[0.2em] mb-2 text-yellow-400">Industrial Advantage</h3>
                  <p className="font-bold text-xs sm:text-sm leading-relaxed">
                    Industrial and maximum demand customers enjoy higher power availability through optimized line realignment, supporting regional economic growth.
                  </p>
                </div>

                <div className="p-5 sm:p-6 bg-black rounded-xl text-white shadow-xl relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-green-700/10 rounded-full -mr-16 -mt-16"></div>
                  <h3 className="text-[10px] font-black uppercase tracking-[0.2em] mb-2 text-green-700">Service Excellence</h3>
                  <p className="font-bold text-xs sm:text-sm leading-relaxed">
                    Our Customer Complaints Unit resolves over 95% of issues within stipulated timeframes using latest digital tracking technology.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Key Initiatives Section */}
        <div className="mb-6">
          <h2 className="text-lg sm:text-xl md:text-2xl font-black text-black mb-4 text-center uppercase tracking-tighter">Key <span className="text-green-700">Initiatives</span></h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            {keyInitiatives.map((initiative, index) => (
              <div key={index} className="bg-white p-4 sm:p-5 rounded-xl border border-gray-100 hover:border-green-700 hover:shadow-lg transition-all duration-300 group">
                <div className="flex flex-col md:flex-row items-center md:items-start gap-4">
                  <div className="bg-gray-50 p-4 rounded-xl text-2xl group-hover:bg-green-700 transition-colors">{initiative.icon}</div>
                  <div className="text-center md:text-left">
                    <h3 className="text-sm sm:text-base font-black text-black mb-2 uppercase tracking-tight group-hover:text-green-700 transition-colors">{initiative.title}</h3>
                    <p className="text-gray-500 font-medium leading-relaxed text-xs sm:text-sm">{initiative.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Upcoming & Media Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <div className="bg-black text-white rounded-xl md:rounded-2xl p-5 sm:p-6 md:p-8 shadow-xl relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-green-700/10 rounded-full -mr-16 -mt-16"></div>
            <h2 className="text-base sm:text-lg md:text-xl font-black uppercase tracking-tighter mb-4 text-green-700">Completed Projects</h2>
            <ul className="space-y-4">
              {completedProjects.map((project, index) => (
                <li key={index} className="flex items-start group">
                  <span className="inline-block w-2.5 h-2.5 bg-yellow-400 rounded-full mt-1.5 mr-3 flex-shrink-0 group-hover:scale-125 transition-transform"></span>
                    <a href={project.link} target="blank">
                     <span className="text-white font-bold text-xs sm:text-sm group-hover:text-yellow-400 transition-colors">{project.name}</span> 
                    </a> 
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-gray-50 rounded-xl md:rounded-2xl p-5 sm:p-6 md:p-8 border border-gray-100 shadow-xl">
            <h2 className="text-base sm:text-lg md:text-xl font-black uppercase tracking-tighter mb-4 text-black">Media Relations</h2>
            <p className="text-gray-600 font-medium mb-6 text-xs sm:text-sm leading-relaxed italic">
              Contact our Corporate Communications team for press releases, interviews,
              and media partnerships.
            </p>
            <div className="space-y-3 mb-6">
              <div className="flex items-center gap-3">
                <span className="text-[10px] font-black uppercase text-gray-400 w-14">Email:</span>
                <span className="font-bold text-black text-xs">customercomplaints@beninelectric.com</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-[10px] font-black uppercase text-gray-400 w-14">Phone:</span>
                <span className="font-bold text-black text-xs sm:text-sm">0700 000 2332</span>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 sm:gap-3">
              {['LinkedIn', 'Twitter', 'Facebook'].map(social => (
                <button key={social} className="px-4 py-2 bg-white border border-gray-200 rounded-lg font-black uppercase tracking-widest text-[10px] hover:border-green-700 hover:text-green-700 transition-all shadow-sm">
                  {social}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Mission Statement */}
        <div className="mt-6 text-center">
          <div className="w-16 h-1.5 bg-yellow-400 mx-auto rounded-full mb-4"></div>
          <p className="text-gray-600 font-black uppercase tracking-widest text-sm sm:text-base md:text-lg max-w-2xl mx-auto italic leading-relaxed">
            "...Committed to Serving you better..."
          </p>
        </div>
      </div>
    </div>
  );
};

export default CorporateCommunications;